import { Link } from "react-router-dom";
import { ArrowRight, TreePine, Bell, Gift } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { HOLIDAY_ENABLED } from "@/constants/site";

const RED = "text-[#9E2B25]";
const RED_HEX = "#9E2B25";

const RED_BUTTON =
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#9E2B25] px-7 py-3 text-sm font-medium tracking-wide text-[#F8F2EA] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:opacity-90";

const GHOST_RED_BUTTON =
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border border-[#9E2B25]/40 px-7 py-3 text-sm font-medium tracking-wide text-[#9E2B25] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#9E2B25] hover:text-[#F8F2EA]";

export default function HolidaySection() {
  if (!HOLIDAY_ENABLED) return null;

  return (
    <section data-testid="holiday-section" className="bg-[#F8F2EA]">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <FadeUp>
          <div className="max-w-3xl">
            <p className={`flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] ${RED}`}>
              <TreePine size={15} aria-hidden="true" /> Thanksgiving &amp; Christmas
            </p>
            <h2 className={`mt-4 font-display text-4xl font-light md:text-5xl ${RED}`}>Sita Holiday Catering</h2>
            <div className="mt-5 h-px w-20 bg-[#E1AA54]" aria-hidden="true" />
            <p className="mt-6 text-lg leading-relaxed text-ink/85">
              Bold, from-scratch holiday food — by the tray, a full dinner, or cooked hot in your own kitchen. Delivery
              in Jersey City and the surrounding area.
            </p>
          </div>
        </FadeUp>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <FadeUp delay={0.05}>
            <div
              data-testid="holiday-card-tray"
              className="flex h-full flex-col rounded-3xl border border-[#9E2B25]/15 bg-white/60 p-8 transition-transform duration-300 hover:-translate-y-1"
            >
              <Gift size={20} style={{ color: RED_HEX }} aria-hidden="true" />
              <div className="mt-5 h-px w-10 bg-[#E1AA54]" aria-hidden="true" />
              <h3 className={`mt-5 font-display text-2xl ${RED}`}>By the Tray</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80">
                Half tray $30 · Full tray $45. Pick your trays online and we'll have them ready — hot and ready to
                serve.
              </p>
              <div className="mt-7">
                <Link to="/holiday-order" data-testid="holiday-tray-order-link" className={RED_BUTTON}>
                  Order by the tray <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div
              data-testid="holiday-card-dinner"
              className="flex h-full flex-col rounded-3xl border border-[#9E2B25]/15 bg-white/60 p-8 transition-transform duration-300 hover:-translate-y-1"
            >
              <Bell size={20} style={{ color: RED_HEX }} aria-hidden="true" />
              <div className="mt-5 h-px w-10 bg-[#E1AA54]" aria-hidden="true" />
              <h3 className={`mt-5 font-display text-2xl ${RED}`}>Full Holiday Dinner</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80">
                Dinner for 15 to 20 guests. We build the menu around your table and your guests, so it fits the event
                you are actually having. Pricing is based on materials and time.
              </p>
              <div className="mt-7">
                <Link to="/inquiries?type=holiday-dinner" data-testid="holiday-dinner-inquire-link" className={GHOST_RED_BUTTON}>
                  Inquire <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.25}>
            <div
              data-testid="holiday-card-inhome"
              className="flex h-full flex-col rounded-3xl border border-[#9E2B25]/15 bg-white/60 p-8 transition-transform duration-300 hover:-translate-y-1"
            >
              <TreePine size={20} style={{ color: RED_HEX }} aria-hidden="true" />
              <div className="mt-5 h-px w-10 bg-[#E1AA54]" aria-hidden="true" />
              <h3 className={`mt-5 font-display text-2xl ${RED}`}>In-Home Cooking</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80">
                We come to you and cook in your kitchen, so everything reaches the table hot. The menu is built around
                your guests and your event, and pricing is based on materials and time.
              </p>
              <div className="mt-7">
                <Link to="/inquiries?type=holiday-inhome" data-testid="holiday-inhome-inquire-link" className={GHOST_RED_BUTTON}>
                  Inquire <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.3}>
          <p className="mt-10 text-sm leading-relaxed text-ink/60">
            Tell us about allergies and we will cook around them. · Alternate contact: DM{" "}
            <a
              href="https://instagram.com/Sita_Earthfound"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="holiday-instagram-link"
              className="underline decoration-[#E1AA54] underline-offset-4 hover:text-[#9E2B25]"
            >
              @Sita_Earthfound
            </a>{" "}
            · Text{" "}
            <a href="sms:5188476836" data-testid="holiday-sms-link" className="underline decoration-[#E1AA54] underline-offset-4 hover:text-[#9E2B25]">
              518-847-6836
            </a>
          </p>
        </FadeUp>
      </div>
    </section>
  );
}